import React from "react"
import { useSorts } from "../../Common/Hooks/useSorts"
import { BlightCardLink } from "../Filtering/BlightCardLink"
import SI, { Play } from "../../Common/SI"
import { SortToggle } from "../SortToggle"
import Sorting from "../Sorting"

export interface IslandStateTableProps {
	plays: Play[]
}

export const IslandStateTable = React.memo(function (props: IslandStateTableProps): JSX.Element {

	function makeRow(title: string, section: number, plays: Play[]): TableRow {

		const wins = plays.filter(play => play.outcome.victory)

		return {
			section,
			title,
			winCount: plays.length ? wins.length : null,
			lossCount: plays.length ? plays.length - wins.length : null,
			playCount: plays.length ? plays.length : null,
			winRate: plays.length ? wins.length / plays.length : null,
		}
	}

	const table = 'IslandState'

	const { sorts } = useSorts()
	const sort = sorts.get(table) || { field: 'setOrder', ascending: true }

	const unknown = props.plays.filter(play => !play.islandState)
	const healthy = props.plays.filter(play => play.islandState?.healthy)
	const veryHealthy = healthy.filter(play => play.islandState!.blightCards.length === 0)
	const stillHealthy = healthy.filter(play => play.islandState!.blightCards.length > 0)
	const blighted = props.plays.filter(play => play.islandState?.healthy === false)

	const healthyHeader = makeRow('Healthy', 1, healthy)
	const healthyTableData: TableRow[] = [
		makeRow('Healthy', 1, veryHealthy),
		makeRow('Still Healthy', 1, stillHealthy),
	]

	const blightedHeader = makeRow('Blighted', 2, blighted)
	const blightedTableData: TableRow[] = SI.blightCards.map(blightCard =>
		makeRow(blightCard, 2, blighted.filter(play => play.islandState!.blightCards.indexOf(blightCard) > -1))
	)

	const unknownHeader = makeRow('[Unknown]', 3, unknown)

	const headers = [
		healthyHeader,
		blightedHeader,
		unknownHeader,
	]

	Sorting.sort(headers, sort, 'section')
	Sorting.sort(healthyTableData, sort, 'title')
	Sorting.sort(blightedTableData, sort, 'title')

	const allDetailRows = [
		...healthyTableData,
		...blightedTableData,
	]

	return (
		<div>
			<table className="stats island-state">
				<thead>
					<tr>
						<th>
							<SortToggle table={table} field={'section'} label='Island State' defaultAscending={true} />
						</th>
						<th>
							<SortToggle table={table} field={'winCount'} label='W' />
						</th>
						<th>
							<SortToggle table={table} field={'lossCount'} label='L' />
						</th>
						<th>
							<SortToggle table={table} field={'playCount'} label='Total' />
						</th>
						<th>
							<SortToggle table={table} field={'winRate'} label='Win %' />
						</th>
					</tr>
				</thead>
				<tbody>
					{
						headers.map(header => (
							<React.Fragment key={header.title}>
								<tr className="subhead">
									<th>{header.title}</th>
									<td className="victory">{header.winCount}</td>
									<td className="loss">{header.lossCount}</td>
									<td>{header.playCount}</td>
									<td>{header.winRate ? `${(100 * header.winRate).toFixed(1)}%` : ''}</td>
								</tr>
								{
									allDetailRows
										.filter(detail => detail.section === header.section)
										.map(detail => (
											<tr className="sub" key={detail.title}>
												<th>{
													SI.blightCards.indexOf(detail.title) === -1
														? detail.title
														: <BlightCardLink blightCard={detail.title} />
												}</th>
												<td className="victory">{detail.winCount}</td>
												<td className="loss">{detail.lossCount}</td>
												<td>{detail.playCount}</td>
												<td>{detail.winRate ? `${(100 * detail.winRate).toFixed(1)}%` : ''}</td>
											</tr>
										))
								}
							</React.Fragment>
						))
					}
				</tbody>
			</table>
		</div>
	)
})

interface TableRow {
	lossCount: number | null
	playCount: number | null
	section: number
	title: string
	winCount: number | null
	winRate: number | null
}
